/**
 * TRL Image Library Tab
 *
 * The Trailblazers module of the Content Library, redesigned as a gallery:
 *  - A navbar with an Add Screenshot button (opens the upload form in a modal),
 *    a search box, and filters (planet/area, author, sharing status, tags).
 *  - A full-width grid of screenshot tiles, each with a gradient caption
 *    overlay. Clicking a tile opens it in a lightbox where the metadata can be
 *    viewed, edited, shared, and deleted.
 *  - Transfer actions: Export bundles up the currently filtered screenshots as
 *    a zip, and Import (super-admins only) brings a bundle in from another
 *    environment — see ../transferApi and ./ImportLibraryModal.
 *
 * Data comes from the `useTrlImageLibrary()` context; this component only owns
 * the view state (search/filters, selected image, add-modal open).
 */

import { useState, useCallback, useEffect } from 'react';
import type { TrlImageAsset } from '@swtorbit/shared-socket-events';
import { useTrlImageLibrary } from './TrlImageLibraryContext';
import { GalleryNavbar } from './GalleryNavbar';
import { GalleryTile } from './GalleryTile';
import { ImageLightbox } from './ImageLightbox';
import { AddScreenshotModal } from './AddScreenshotModal';
import { StatsModal } from './StatsModal';
import { ImportLibraryModal } from './ImportLibraryModal';
import { exportEntireTrlLibrary, exportTrlBundle } from '../transferApi';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useGalleryFilters, EMPTY_FILTERS, DEFAULT_SORT, type GalleryFilters, type GallerySort, type SharingFilter, type SortDirection, type SortField } from './useGalleryFilters';

/**
 * Persist the gallery filters + sort across reloads. Follows the app's
 * `swtorbit.trl.*` localStorage convention (see MetadataForm's LAST_LOCATION_KEY)
 * and the validate-on-read pattern used by authStorage / GameContext — an unknown
 * or malformed stored value cleanly falls back to the corresponding default.
 */
const GALLERY_FILTERS_KEY = 'swtorbit.trl.galleryFilters';
const GALLERY_SORT_KEY = 'swtorbit.trl.gallerySort';

const SORT_FIELDS: readonly SortField[] = ['createdAt', 'area', 'name', 'author', 'difficulty', 'faction'];
const SORT_DIRECTIONS: readonly SortDirection[] = ['asc', 'desc'];
const SHARING_FILTERS: readonly SharingFilter[] = ['all', 'private', 'shared-by-me', 'shared-by-others'];

function readStoredFilters(): GalleryFilters {
  try {
    const raw = localStorage.getItem(GALLERY_FILTERS_KEY);
    if (!raw) return EMPTY_FILTERS;
    const parsed = JSON.parse(raw) as Partial<GalleryFilters>;
    if (parsed && typeof parsed === 'object') {
      return {
        search: typeof parsed.search === 'string' ? parsed.search : EMPTY_FILTERS.search,
        tags: Array.isArray(parsed.tags) ? parsed.tags.filter((t): t is string => typeof t === 'string') : EMPTY_FILTERS.tags,
        area: typeof parsed.area === 'string' ? parsed.area : null,
        author: typeof parsed.author === 'string' ? parsed.author : null,
        sharing: SHARING_FILTERS.includes(parsed.sharing as SharingFilter) ? (parsed.sharing as SharingFilter) : EMPTY_FILTERS.sharing,
      };
    }
  } catch {
    /* malformed JSON or storage unavailable — fall through to default */
  }
  return EMPTY_FILTERS;
}

function readStoredSort(): GallerySort {
  try {
    const raw = localStorage.getItem(GALLERY_SORT_KEY);
    if (!raw) return DEFAULT_SORT;
    const parsed = JSON.parse(raw) as Partial<GallerySort>;
    if (
      parsed &&
      SORT_FIELDS.includes(parsed.field as SortField) &&
      SORT_DIRECTIONS.includes(parsed.direction as SortDirection)
    ) {
      return { field: parsed.field as SortField, direction: parsed.direction as SortDirection };
    }
  } catch {
    /* malformed JSON or storage unavailable — fall through to default */
  }
  return DEFAULT_SORT;
}

export function TrlImageLibraryTab() {
  const { images, imagesLoading, imagesError, refreshImages } = useTrlImageLibrary();
  const { isSuperAdmin } = useAuthContext();

  const [filters, setFilters] = useState<GalleryFilters>(readStoredFilters);
  const [sort, setSort] = useState<GallerySort>(readStoredSort);
  const [selected, setSelected] = useState<TrlImageAsset | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [prefillFile, setPrefillFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const visibleImages = useGalleryFilters(images, filters, sort);

  // The image library lives in an app-root context (so the round settings
  // picker can use it without the Content Library ever being opened), so it
  // only fetches once on app load. This tab unmounts/remounts every time the
  // Content Library view is entered — refetch then, so usage counts bumped by
  // a game start or reset by an admin elsewhere aren't stuck showing stale data.
  useEffect(() => {
    void refreshImages();
  }, [refreshImages]);

  // Remember the chosen filters + sort across reloads.
  useEffect(() => {
    localStorage.setItem(GALLERY_FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem(GALLERY_SORT_KEY, JSON.stringify(sort));
  }, [sort]);

  // Keep the open lightbox in sync with refreshed data (edits/share changes).
  const selectedImage = selected ? images.find((i) => i.id === selected.id) ?? null : null;

  const openAddWithFile = useCallback((f: File) => {
    setPrefillFile(f);
    setIsAddOpen(true);
  }, []);

  const handleAddClose = useCallback(() => {
    setIsAddOpen(false);
    setPrefillFile(null);
  }, []);

  /**
   * Export exactly what the gallery currently shows. The filtering is
   * client-side, so posting the visible ids is both simpler than re-deriving the
   * filters server-side and guaranteed to match what the admin is looking at.
   */
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setTransferError(null);
    try {
      await exportTrlBundle(visibleImages.map((image) => image.id));
    } catch (err) {
      setTransferError(err instanceof Error ? err.message : 'Failed to export bundle');
    } finally {
      setIsExporting(false);
    }
  }, [visibleImages]);

  /**
   * Whole-library export (super-admins only). Deliberately does not send the
   * visible ids: it also covers other admins' private screenshots, which are
   * absent from this gallery, so the server picks the set.
   */
  const handleExportAll = useCallback(async () => {
    setIsExporting(true);
    setTransferError(null);
    try {
      await exportEntireTrlLibrary();
    } catch (err) {
      setTransferError(err instanceof Error ? err.message : 'Failed to export library');
    } finally {
      setIsExporting(false);
    }
  }, []);

  // Clipboard paste → open add modal with prefill (only when modal is not already open).
  useEffect(() => {
    if (isAddOpen) return;
    const handlePaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) return;
      const imageItem = Array.from(e.clipboardData?.items ?? []).find(
        (item) => item.type.startsWith('image/'),
      );
      if (!imageItem) return;
      e.preventDefault();
      const f = imageItem.getAsFile();
      if (f) openAddWithFile(f);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isAddOpen, openAddWithFile]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      openAddWithFile(file);
    }
  }, [openAddWithFile]);

  return (
    <div
      className="text-white d-flex flex-column flex-1 gap-sm"
      style={{ width: '100%', position: 'relative', minHeight: 0 }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="gallery-drop-overlay">
          <span className="gallery-drop-overlay__label">Drop to add screenshot</span>
        </div>
      )}

      <GalleryNavbar
        images={images}
        filters={filters}
        onFiltersChange={setFilters}
        sort={sort}
        onSortChange={setSort}
        onAdd={() => setIsAddOpen(true)}
        onRefresh={() => void refreshImages()}
        onStats={() => setIsStatsOpen(true)}
        onExport={() => void handleExport()}
        onExportAll={isSuperAdmin ? () => void handleExportAll() : undefined}
        isExporting={isExporting}
        onImport={isSuperAdmin ? () => setIsImportOpen(true) : undefined}
        resultCount={visibleImages.length}
      />

      {imagesError && (
        <div className="bg-space bg-o-50 border-md border-red rounded-sm p-xs">
          <span className="text-red fs-sm">{imagesError}</span>
        </div>
      )}

      {transferError && (
        <div className="bg-space bg-o-50 border-md border-red rounded-sm p-xs">
          <span className="text-red fs-sm">{transferError}</span>
        </div>
      )}

      {imagesLoading && images.length === 0 ? (
        <p className="text-center text-white m-0" style={{ padding: '3rem 0' }}>Loading screenshots…</p>
      ) : visibleImages.length === 0 ? (
        <p className="text-center text-white m-0" style={{ padding: '3rem 0' }}>
          {images.length === 0 ? 'No screenshots yet. Add one to get started.' : 'No screenshots match your filters.'}
        </p>
      ) : (
        <div className="gallery">
          {visibleImages.map((image) => (
            <GalleryTile key={image.id} image={image} onOpen={setSelected} />
          ))}
        </div>
      )}

      {selectedImage && <ImageLightbox image={selectedImage} onClose={() => setSelected(null)} />}
      <AddScreenshotModal isOpen={isAddOpen} onClose={handleAddClose} prefillFile={prefillFile} />
      <StatsModal isOpen={isStatsOpen} onClose={() => setIsStatsOpen(false)} images={images} />
      {isSuperAdmin && (
        <ImportLibraryModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          onImported={() => void refreshImages()}
        />
      )}
    </div>
  );
}
